import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Package, Heart, User, Trash2, Gift, Copy, CheckCircle2, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import LoyaltyPanel from "../components/LoyaltyPanel";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { resolveMediaUrl } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  nouveau: "Nouveau",
  en_attente_paiement: "En attente de paiement",
  paye: "Payée",
  en_preparation: "En préparation",
  expediee: "Expédiée",
  livree: "Livrée",
  annulee: "Annulée",
};
const STATUS_COLOR = {
  paye: "text-[color:var(--gold)]",
  expediee: "text-emerald-400",
  livree: "text-emerald-400",
  annulee: "text-red-400",
  en_attente_paiement: "text-orange-400",
  nouveau: "text-white/60",
  en_preparation: "text-blue-400",
};

export default function AccountDashboard() {
  const { user, loading, logout } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get("tab");
      if (t && ["orders", "loyalty", "wishlist", "profile"].includes(t)) return t;
    } catch { /* noop */ }
    return "orders";
  });
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [stats, setStats] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user) return;
    setDataLoading(true);
    try {
      const [ord, wl, st] = await Promise.all([
        axios.get(`${API}/customers/orders`, { withCredentials: true }),
        axios.get(`${API}/customers/wishlist`, { withCredentials: true }),
        axios.get(`${API}/customers/stats`, { withCredentials: true }),
      ]);
      setOrders(ord.data || []);
      setWishlist(wl.data || []);
      setStats(st.data || null);
    } catch (e) {
      toast.error("Impossible de charger vos données");
    } finally {
      setDataLoading(false);
    }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const onLogout = async () => {
    await logout();
    toast.success("À bientôt.");
    navigate("/");
  };

  const removeWish = async (productId) => {
    try {
      await axios.delete(`${API}/customers/wishlist/${productId}`, { withCredentials: true });
      setWishlist((prev) => prev.filter((p) => p.id !== productId));
      toast.success("Retiré de la wishlist");
    } catch {
      toast.error("Impossible de retirer");
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white/40">Chargement…</div>;
  if (!user) return <Navigate to="/connexion" state={{ from: "/compte" }} replace />;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="account-dashboard">
      <Navbar />
      <div className="pt-40 pb-24 px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="label-caps text-[color:var(--gold)] text-[11px] tracking-[0.3em] mb-2">Espace client</p>
            <h1 className="font-serif text-4xl md:text-5xl" data-testid="account-hello">
              Bonjour, <em className="text-[color:var(--gold)]">{user.full_name?.split(" ")[0] || "cher client"}</em>
            </h1>
            <p className="text-white/50 text-sm mt-2">{user.email}</p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            className="border border-white/20 hover:border-[color:var(--gold)] px-6 py-3 label-caps text-[11px] tracking-[0.25em] flex items-center gap-2 transition-colors"
          >
            <LogOut size={14} /> Déconnexion
          </button>
        </div>

        <WelcomeCoupon coupon={user.welcome_coupon} firstName={user.full_name?.split(" ")[0]} />

        {stats && (stats.orders_count > 0 || stats.active_gift_cards.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-testid="customer-stats">
            <div className="border border-white/10 p-5">
              <p className="label-caps text-[10px] text-white/40 tracking-[0.25em]">Total dépensé</p>
              <p className="font-serif text-3xl text-[color:var(--gold)] mt-2" data-testid="stats-total-spent">
                {formatPrice(stats.total_spent)}
              </p>
              <p className="text-white/40 text-xs mt-1">sur {stats.orders_count} commande{stats.orders_count > 1 ? "s" : ""}</p>
            </div>
            <div className="border border-white/10 p-5">
              <p className="label-caps text-[10px] text-white/40 tracking-[0.25em]">Statut</p>
              <p className="font-serif text-3xl text-white mt-2">
                {stats.orders_count >= 5 ? "Prestige" : stats.orders_count >= 3 ? "Signature" : stats.orders_count >= 1 ? "Le Cercle" : "Découverte"}
              </p>
              <p className="text-white/40 text-xs mt-1">Fidélité MAISON DOUBA</p>
            </div>
            <div className="border border-[color:var(--gold)]/30 bg-[color:var(--gold)]/[0.03] p-5">
              <p className="label-caps text-[10px] text-[color:var(--gold)] tracking-[0.25em]">Cartes cadeaux actives</p>
              {stats.active_gift_cards.length === 0 ? (
                <p className="text-white/40 text-sm mt-2 italic">Aucune carte reçue</p>
              ) : (
                <div className="mt-2 space-y-2">
                  {stats.active_gift_cards.slice(0, 2).map((gc) => (
                    <div key={gc.code} data-testid={`gift-card-${gc.code}`}>
                      <p className="font-mono text-sm text-[color:var(--gold)] tracking-[0.15em]">{gc.code}</p>
                      <p className="text-white/60 text-xs mt-0.5">
                        <span className="text-white font-serif text-lg">{formatPrice(gc.remaining_amount)}</span>
                        {gc.buyer_name && <span className="text-white/40 ml-2 text-[10px]">· offert par {gc.buyer_name}</span>}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2 border-b border-white/10 mb-10 overflow-x-auto">
          <TabButton active={tab === "orders"} onClick={() => setTab("orders")} testid="tab-orders" icon={<Package size={14} />}>
            Commandes ({orders.length})
          </TabButton>
          <TabButton active={tab === "loyalty"} onClick={() => setTab("loyalty")} testid="tab-loyalty" icon={<Sparkles size={14} />}>
            Fidélité
          </TabButton>
          <TabButton active={tab === "wishlist"} onClick={() => setTab("wishlist")} testid="tab-wishlist" icon={<Heart size={14} />}>
            Wishlist ({wishlist.length})
          </TabButton>
          <TabButton active={tab === "profile"} onClick={() => setTab("profile")} testid="tab-profile" icon={<User size={14} />}>
            Profil
          </TabButton>
        </div>

        {dataLoading && <p className="text-white/40 text-center py-12">Chargement…</p>}

        {!dataLoading && tab === "orders" && (
          <div data-testid="orders-list">
            {orders.length === 0 ? (
              <EmptyState title="Aucune commande" text="Découvrez notre collection et laissez-vous tenter." cta="/#collection" ctaLabel="Explorer" />
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className="border border-white/10 hover:border-white/25 p-6 transition-colors" data-testid={`order-${o.id.slice(0,8)}`}>
                    <div className="flex items-start justify-between flex-wrap gap-4">
                      <div>
                        <p className="label-caps text-[10px] text-white/40 tracking-[0.25em]">Commande</p>
                        <p className="font-serif text-2xl mt-1">#{o.id.slice(0, 8).toUpperCase()}</p>
                        <p className="text-white/50 text-xs mt-2">{new Date(o.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-serif text-3xl text-[color:var(--gold)]">{formatPrice(o.total)}</p>
                        <p className={`label-caps text-[10px] tracking-[0.25em] mt-2 ${STATUS_COLOR[o.status] || "text-white/60"}`}>
                          {STATUS_LABEL[o.status] || o.status}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10 text-sm text-white/70">
                      {(o.items || []).map((i, idx) => (
                        <div key={idx} className="flex justify-between py-1">
                          <span>{i.name} <span className="text-white/40">· {i.volume} · ×{i.quantity}</span></span>
                          <span>{formatPrice(i.price * i.quantity)}</span>
                        </div>
                      ))}
                    </div>
                    {o.tracking_number && (
                      <div className="mt-4 pt-4 border-t border-white/10 text-xs text-white/60">
                        <span className="label-caps tracking-[0.25em] text-[color:var(--gold)] mr-2">Suivi :</span>
                        <span className="font-mono">{o.tracking_number}</span>
                        {o.carrier_name && <span className="ml-2 text-white/40">· {o.carrier_name}</span>}
                      </div>
                    )}
                    <RetractationSection order={o} onRequested={loadData} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!dataLoading && tab === "loyalty" && (
          <LoyaltyPanel />
        )}

        {!dataLoading && tab === "wishlist" && (
          <div data-testid="wishlist-list">
            {wishlist.length === 0 ? (
              <EmptyState title="Wishlist vide" text="Ajoutez vos parfums coup de cœur pour les retrouver ici." cta="/#collection" ctaLabel="Découvrir" />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {wishlist.map((p) => (
                  <div key={p.id} className="group relative" data-testid={`wish-${p.slug}`}>
                    <Link to={`/parfum/${p.slug}`}>
                      <div className="aspect-[3/4] overflow-hidden bg-white/5">
                        <img src={resolveMediaUrl(p.image_main)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                      <p className="mt-3 label-caps text-[10px] text-white/40 tracking-[0.25em]">{p.brand}</p>
                      <p className="font-serif text-lg mt-1">{p.name}</p>
                      <p className="text-[color:var(--gold)] text-sm mt-1">{formatPrice(p.price_100ml)}</p>
                    </Link>
                    <button
                      onClick={() => removeWish(p.id)}
                      className="absolute top-2 right-2 bg-black/80 border border-white/20 hover:border-red-400 p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Retirer"
                      data-testid={`remove-wish-${p.slug}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!dataLoading && tab === "profile" && (
          <div className="max-w-xl" data-testid="profile-panel">
            <div className="space-y-6">
              <ProfileField label="Nom" value={user.full_name} />
              <ProfileField label="Email" value={user.email} />
              <ProfileField label="Compte créé le" value={new Date(user.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} />
            </div>
            <p className="text-white/40 text-xs mt-8">
              Pour modifier vos informations, contactez-nous à{" "}
              <a href="mailto:maisondouba@wwwmaisondouba.com" className="text-[color:var(--gold)] hover:underline">maisondouba@wwwmaisondouba.com</a>
            </p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const TabButton = ({ active, onClick, testid, icon, children }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-5 py-3 label-caps text-[11px] tracking-[0.25em] flex items-center gap-2 border-b-2 -mb-px transition-colors ${
      active ? "border-[color:var(--gold)] text-white" : "border-transparent text-white/50 hover:text-white/80"
    }`}
  >
    {icon} {children}
  </button>
);

const ProfileField = ({ label, value }) => (
  <div>
    <p className="label-caps text-[10px] text-white/40 tracking-[0.25em] mb-1">{label}</p>
    <p className="font-serif text-xl">{value || "—"}</p>
  </div>
);

const EmptyState = ({ title, text, cta, ctaLabel }) => (
  <div className="text-center py-24">
    <h3 className="font-serif text-3xl mb-3">{title}</h3>
    <p className="text-white/50 text-sm mb-8">{text}</p>
    <Link to={cta} className="inline-block bg-[color:var(--gold)] text-black px-8 py-3 label-caps text-[11px] tracking-[0.3em] hover:bg-white transition-colors">
      {ctaLabel}
    </Link>
  </div>
);


const WelcomeCoupon = ({ coupon, firstName }) => {
  const [copied, setCopied] = useState(false);
  if (!coupon || coupon.used) return null;

  const expiresAt = coupon.expires_at ? new Date(coupon.expires_at) : null;
  const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24))) : null;

  const copy = () => {
    navigator.clipboard.writeText(coupon.code).then(
      () => { setCopied(true); toast.success("Code copié — appliquez-le au moment du paiement"); setTimeout(() => setCopied(false), 2500); },
      () => toast.error("Copie impossible")
    );
  };

  return (
    <div
      data-testid="welcome-coupon"
      className="relative overflow-hidden border border-[color:var(--gold)]/40 bg-gradient-to-br from-[color:var(--gold)]/10 via-[#0A0A0A] to-black p-6 md:p-8 mb-12"
    >
      <div className="absolute -top-8 -right-8 opacity-10 pointer-events-none">
        <Gift size={140} strokeWidth={0.8} className="text-[color:var(--gold)]" />
      </div>

      <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
        <div className="w-14 h-14 rounded-full bg-[color:var(--gold)]/15 border border-[color:var(--gold)]/40 flex items-center justify-center shrink-0">
          <Gift size={22} className="text-[color:var(--gold)]" strokeWidth={1.5} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-1">Cadeau de bienvenue</p>
          <h2 className="font-serif text-2xl md:text-3xl mb-1">
            {firstName ? <>Un présent pour vous, <em className="text-[color:var(--gold)]">{firstName}</em></> : "Un présent pour votre première visite"}
          </h2>
          <p className="text-white/60 text-sm">
            <span className="text-white">-{coupon.discount_percent}%</span> sur votre première commande — validez le code au paiement.
            {daysLeft !== null && daysLeft <= 60 && (
              <span className="text-white/40"> · Valable {daysLeft} jour{daysLeft > 1 ? "s" : ""} encore.</span>
            )}
          </p>
        </div>

        <div className="flex items-stretch gap-2">
          <div className="border border-dashed border-[color:var(--gold)]/50 bg-black/40 px-5 py-3 flex items-center gap-2 font-mono tracking-[0.35em] text-[color:var(--gold)] text-lg">
            {coupon.code}
          </div>
          <button
            onClick={copy}
            className="border border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[color:var(--gold)] hover:text-black px-4 py-3 label-caps text-[10px] tracking-[0.25em] transition-colors flex items-center gap-2"
            data-testid="copy-welcome-code"
          >
            {copied ? <><CheckCircle2 size={12} /> Copié</> : <><Copy size={12} /> Copier</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const RETRACTATION_ELIGIBLE = ["expedie", "expediee", "prepare", "livree", "livre"];

const RetractationSection = ({ order, onRequested }) => {
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const alreadyRequested = !!order.retraction_requested_at;
  const eligibleStatus = RETRACTATION_ELIGIBLE.includes(order.status);

  // Compute days remaining (14 days after delivery or creation)
  const refIso = order.delivered_at || order.created_at;
  const refDate = refIso ? new Date(refIso) : null;
  const deadline = refDate ? new Date(refDate.getTime() + 14 * 24 * 3600 * 1000) : null;
  const daysLeft = deadline ? Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const withinWindow = deadline ? new Date() <= deadline : false;

  if (alreadyRequested) {
    return (
      <div className="mt-4 pt-4 border-t border-white/10 text-xs" data-testid={`retraction-status-${order.id.slice(0, 8)}`}>
        <p className="label-caps text-amber-300 tracking-[0.25em] mb-1">Rétractation demandée</p>
        <p className="text-white/50">
          Demande enregistrée le {new Date(order.retraction_requested_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}.
          Nous vous adresserons l'étiquette de retour sous 48h ouvrées.
        </p>
      </div>
    );
  }

  if (!eligibleStatus || !withinWindow) return null;

  const submit = async () => {
    if (!confirmed) { toast.error("Veuillez cocher la confirmation."); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/customers/orders/${order.id}/retraction`,
        { reason: reason.trim() },
        { withCredentials: true }
      );
      toast.success(data.message || "Demande enregistrée");
      setShowModal(false);
      onRequested?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'enregistrer votre demande");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-white/50">
          <span className="label-caps text-[color:var(--gold)] tracking-[0.2em] mr-2">Droit de rétractation</span>
          <span>Valable encore {daysLeft} jour{daysLeft > 1 ? "s" : ""} (article L221-18)</span>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="border border-white/20 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] px-4 py-2 label-caps text-[10px] tracking-[0.25em] transition-colors"
          data-testid={`retract-btn-${order.id.slice(0, 8)}`}
        >
          Demander la rétractation
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/85 z-[150] flex items-center justify-center p-4" data-testid="retraction-modal">
          <div className="bg-[#0A0A0A] border border-[color:var(--gold)]/30 max-w-lg w-full p-8">
            <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-3">Rétractation légale</p>
            <h3 className="font-serif text-2xl mb-4">Commande #{order.id.slice(0, 8).toUpperCase()}</h3>

            <p className="text-white/70 text-sm leading-relaxed mb-5">
              Conformément à l'<em>article L221-18 du Code de la consommation</em>, vous disposez d'un délai de <strong>14 jours</strong> à compter de la réception de votre commande pour vous rétracter sans motif ni pénalité.
            </p>
            <p className="text-white/60 text-xs mb-6">
              L'article doit être <strong>scellé, dans son emballage d'origine</strong>. Le remboursement intégral est effectué sous 14 jours après réception du colis.
            </p>

            <label className="block mb-4">
              <span className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-2">Motif (facultatif)</span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="Vous n'êtes pas obligé de motiver votre demande…"
                className="w-full bg-transparent border border-white/15 focus:border-[color:var(--gold)] px-3 py-2 text-sm outline-none"
                data-testid="retract-reason"
              />
            </label>

            <label className="flex items-start gap-2 mb-6 text-xs text-white/70 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-0.5 accent-[color:var(--gold)]"
                data-testid="retract-confirm"
              />
              <span>Je confirme demander l'exercice de mon droit de rétractation. J'accepte de renvoyer l'article scellé et intact sous 14 jours après cette demande.</span>
            </label>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="border border-white/20 text-white/70 hover:text-white px-4 py-2 label-caps text-[10px] tracking-[0.25em]"
                data-testid="retract-cancel"
              >
                Annuler
              </button>
              <button
                onClick={submit}
                disabled={submitting || !confirmed}
                className="bg-[color:var(--gold)] text-black hover:bg-white px-6 py-2 label-caps text-[10px] tracking-[0.25em] disabled:opacity-40 flex items-center gap-2"
                data-testid="retract-submit"
              >
                {submitting ? "Envoi…" : "Confirmer la demande"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

